import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { parseApiError } from '@saas-kit/lib';
import { Button, Select, Spinner, toast } from '@saas-kit/ui';

import { fetchDisplayMedia, uploadDisplayMedia } from './apiDisplays';
import type { BrandingFormState } from './displayFormState';
import type { QmsBrandingMode } from './types';

const MODES: QmsBrandingMode[] = ['none', 'image', 'video'];
const IMAGE_ACCEPT = 'image/png,image/jpeg,image/webp';
const VIDEO_ACCEPT = 'video/mp4,video/webm';
// Upload ceilings, mirrored from the backend defaults (QMS_DISPLAY_*_MAX_BYTES).
// Checked client-side so an oversized file is rejected instantly with a clear
// message, instead of failing mid-upload with a generic error.
const IMAGE_MAX_BYTES = 10 * 1024 * 1024;
const VIDEO_MAX_BYTES = 200 * 1024 * 1024;

/**
 * Branding media picker for the display setup form: the user selects a mode
 * (none / image / video) and uploads the file shown on the public board.
 */
export function DisplayMediaField({
  value,
  onChange,
}: {
  value: BrandingFormState;
  onChange: (next: BrandingFormState) => void;
}): JSX.Element {
  const { t } = useTranslation('qms');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!value.file_id) {
      setPreviewUrl(null);
      return;
    }
    let cancelled = false;
    void fetchDisplayMedia(value.file_id)
      .then((media) => {
        if (!cancelled) setPreviewUrl(media.download_url);
      })
      .catch(() => {
        if (!cancelled) setPreviewUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [value.file_id]);

  const isImage = value.mode === 'image';
  const isVideo = value.mode === 'video';

  const pick = async (file: File): Promise<void> => {
    const limitBytes = value.mode === 'video' ? VIDEO_MAX_BYTES : IMAGE_MAX_BYTES;
    if (file.size > limitBytes) {
      toast.error(
        t('displays.branding.too_large', {
          limit: Math.round(limitBytes / (1024 * 1024)),
        }),
      );
      return;
    }
    setUploading(true);
    try {
      const media = await uploadDisplayMedia(file);
      onChange({ ...value, file_id: media.id });
      setPreviewUrl(media.download_url);
      toast.success(t('displays.branding.uploaded'));
    } catch (error) {
      toast.error(parseApiError(error).message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-3 rounded-md border border-border p-3">
      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {t('displays.branding.title')}
      </p>
      <label className="block space-y-1 text-sm">
        <span className="text-foreground">{t('displays.branding.mode')}</span>
        <Select
          value={value.mode}
          onChange={(e) => {
            const mode = e.target.value as QmsBrandingMode;
            onChange({ mode, file_id: mode === 'none' ? '' : value.file_id });
          }}
          data-testid="display-branding-mode"
        >
          {MODES.map((mode) => (
            <option key={mode} value={mode}>
              {t(`displays.branding.modes.${mode}`)}
            </option>
          ))}
        </Select>
      </label>
      {isImage || isVideo ? (
        <div className="space-y-2">
          {previewUrl ? (
            isImage ? (
              <img
                src={previewUrl}
                alt=""
                aria-hidden="true"
                className="h-32 w-auto rounded-md border border-border object-cover"
                data-testid="display-branding-preview"
              />
            ) : (
              <video
                src={previewUrl}
                muted
                loop
                autoPlay
                playsInline
                className="h-32 w-auto rounded-md border border-border object-cover"
                data-testid="display-branding-preview"
              />
            )
          ) : null}
          <input
            ref={inputRef}
            type="file"
            accept={isImage ? IMAGE_ACCEPT : VIDEO_ACCEPT}
            className="sr-only"
            data-testid="display-branding-input"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void pick(file);
              e.target.value = '';
            }}
          />
          <Button
            type="button"
            variant="secondary"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
          >
            {uploading ? <Spinner size="sm" /> : null}
            {t(value.file_id ? 'displays.branding.replace' : 'displays.branding.upload')}
          </Button>
          <p className="text-xs text-muted-foreground">
            {t(isImage ? 'displays.branding.image_help' : 'displays.branding.video_help')}
          </p>
        </div>
      ) : null}
    </div>
  );
}
